import { useCallback } from 'react'
import { type ClientFeatureType } from 'shared/api/clients'
import { CLIENT_FEATURE_TO_ROUTE_MAP, INTERNAL_FEATURE_TO_ROUTE_MAP, getFeatureIdFromRoute } from 'shared/routes'
import { useCurrentClient } from 'shared/context/ClientContext'
import { useShouldShowInternalFeatures } from './useShouldShowInternalFeatures'

interface CLientAccessCheckResult {
  featureId: ClientFeatureType
  hasAccess: boolean
}

interface ClientAccessCheckParams {
  featureId?: ClientFeatureType
  route?: string
}

export const useHasClientFeatureAccess = (): {
  checkClientAccessToFeature: ({ featureId, route }: ClientAccessCheckParams) => CLientAccessCheckResult | null
} => {
  const { currentClient } = useCurrentClient()

  const checkClientAccessToFeature = useCallback(
    ({ featureId, route }: ClientAccessCheckParams): CLientAccessCheckResult | null => {
      const parsedFeatureId = featureId ?? getFeatureIdFromRoute(route, CLIENT_FEATURE_TO_ROUTE_MAP)

      if (parsedFeatureId == null) return null

      const hasAccess = currentClient.featurePlan?.[parsedFeatureId]?.enabled ?? false

      return {
        featureId: parsedFeatureId,
        hasAccess
      }
    },
    [currentClient]
  )

  return {
    checkClientAccessToFeature
  }
}

const INTERNAL_USER_FEATURES = ['workspace_setup', 'mimir'] as const

export type InternalFeatureType = (typeof INTERNAL_USER_FEATURES)[number]

interface UserAccessCheckResult {
  featureId: InternalFeatureType
  hasAccess: boolean
}

interface UserAccessCheckParams {
  featureId?: InternalFeatureType
  route?: string
}

export const useHasInternalUserFeatureAccess = (): {
  checkUserAccessToInternalFeature: ({ featureId, route }: UserAccessCheckParams) => UserAccessCheckResult | null
} => {
  const shouldShowInternalFeatures = useShouldShowInternalFeatures()

  const checkUserAccessToInternalFeature = useCallback(
    ({ featureId, route }: UserAccessCheckParams): UserAccessCheckResult | null => {
      const parsedFeatureId = featureId ?? getFeatureIdFromRoute(route, INTERNAL_FEATURE_TO_ROUTE_MAP)
      if (parsedFeatureId == null) return null

      const userHasInternalFeatureAccess = shouldShowInternalFeatures ?? false

      const isInternalFeature = INTERNAL_USER_FEATURES.includes(parsedFeatureId)

      const hasAccess = userHasInternalFeatureAccess || (!userHasInternalFeatureAccess && !isInternalFeature)

      return {
        hasAccess,
        featureId: parsedFeatureId
      }
    },
    [shouldShowInternalFeatures]
  )

  return {
    checkUserAccessToInternalFeature
  }
}
